import { useState } from 'react';

const DB_NAME = 'AssetRedirectsDB';
const STORE_NAME = 'assets';
const STATUS_ID_IN_USE = 'e4bc306a-0201-42e9-99c6-7fbfdc8415a9';

/**
 * Hook for downloading asset redirect rules
 * Extracts the download logic to be used without UI component
 */
export const useAssetRedirectDownload = () => {
  const [loading, setLoading] = useState(false);

  // Open/create IndexedDB
  const openDB = () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
    });
  };

  // Fetch assets from API with cursor-based pagination
  const fetchAssetsBatch = async (projectId, token, cursorState = null, limit = 200) => {
    const url = new URL(`https://developer.api.autodesk.com/construction/assets/v2/projects/${projectId}/assets`);
    url.searchParams.set('limit', limit);
    url.searchParams.set('filter[statusId]', STATUS_ID_IN_USE);

    if (cursorState) {
      url.searchParams.set('cursorState', cursorState);
    }

    console.log(`[Fetch] Calling API: cursor=${cursorState ? 'yes' : 'none'}, limit=${limit}`);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API failed (${response.status}): ${errorText}`);
    }

    const data = await response.json();
    console.log(`[Fetch] Response: ${data.results?.length || 0} assets, pagination:`, data.pagination);
    return {
      assets: data.results || [],
      pagination: data.pagination || {}
    };
  };

  // Store assets in IndexedDB
  const storeAssets = async (db, assets) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    for (const asset of assets) {
      store.put(asset);
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  };

  // Get all assets from IndexedDB
  const getAllAssets = async (db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  };

  // Clear IndexedDB
  const clearDB = async (db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  };

  // Generate redirect rules from assets
  const generateRedirectRules = (assets, projectId) => {
    const rules = [];
    const skipped = [];

    for (const asset of assets) {
      const clientAssetId = asset.clientAssetId || '';
      const assetId = asset.id;
      const assetName = asset.name || 'Unknown';

      // Extract equipment number from clientAssetId
      const numberMatch = clientAssetId.match(/^(\d+)/);

      if (numberMatch && assetId) {
        const equipmentNumber = numberMatch[1];
        const redirectUrl = `https://acc.autodesk.com/build/assets/projects/${projectId}/assets?assetId=${assetId}`;
        const rule = `Redirect 301 /${equipmentNumber} ${redirectUrl}`;
        rules.push(rule);
      } else {
        skipped.push({ assetId, assetName, clientAssetId });
      }
    }

    return { rules, skipped };
  };

  const downloadAssetRedirects = async (projectId, token) => {
    if (!token) {
      console.error('[Asset Download] No token provided');
      return;
    }

    if (!projectId) {
      console.error('[Asset Download] No project ID provided');
      return;
    }

    setLoading(true);
    console.log('[Asset Download] Starting download for project:', projectId);

    let db;

    try {
      // Open IndexedDB
      db = await openDB();
      await clearDB(db);

      // Fetch assets using cursor-based pagination to get 500 assets
      const limit = 200;
      const maxAssets = 500;
      let totalFetched = 0;
      let cursorState = null;
      const seenIds = new Set();
      let callNum = 0;

      console.log('[Asset Download] Fetching assets using cursor pagination...');

      while (totalFetched < maxAssets && callNum < 10) {
        callNum++;
        console.log(`[Asset Download] Fetching batch ${callNum} (${totalFetched}/${maxAssets} assets)...`);

        const { assets, pagination } = await fetchAssetsBatch(projectId, token, cursorState, limit);

        if (assets.length === 0) {
          console.log(`[Batch ${callNum}] No more assets returned, stopping`);
          break;
        }

        // Filter duplicates
        const uniqueAssets = assets.filter(asset => {
          if (asset.id && !seenIds.has(asset.id)) {
            seenIds.add(asset.id);
            return true;
          }
          return false;
        });

        console.log(`[Batch ${callNum}] Got ${assets.length} assets, ${uniqueAssets.length} unique. Total: ${totalFetched + uniqueAssets.length}`);

        // Store in IndexedDB
        if (uniqueAssets.length > 0) {
          await storeAssets(db, uniqueAssets);
          totalFetched += uniqueAssets.length;
        }

        // Check if there's a next cursor
        if (pagination.cursorState) {
          cursorState = pagination.cursorState;
        } else {
          console.log(`[Batch ${callNum}] No cursor, reached end`);
          break;
        }

        // Stop if we have enough
        if (totalFetched >= maxAssets) {
          console.log(`[Batch ${callNum}] Reached ${maxAssets} assets, stopping`);
          break;
        }
      }

      console.log(`[Asset Download] Fetched ${totalFetched} assets. Generating redirect rules...`);

      // Get all assets from IndexedDB
      const allAssets = await getAllAssets(db);

      // Generate redirect rules
      const { rules, skipped } = generateRedirectRules(allAssets, projectId);

      console.log(`[Asset Download] Generated ${rules.length} redirect rules. Downloading file...`);

      // Create file content - only redirect rules, no headers
      const content = rules.join('\n');

      // Download file
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `asset-redirects-${projectId}-${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Clear IndexedDB
      console.log('[Asset Download] Cleaning up...');
      await clearDB(db);
      db.close();

      console.log(`✅ Download complete! ${rules.length} redirect rules generated.`);

    } catch (err) {
      console.error('[Asset Download] Error:', err);
    } finally {
      if (db) {
        db.close();
      }
      setLoading(false);
    }
  };

  return { downloadAssetRedirects, loading };
};
